import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { format, isSameWeek, isToday, isValid, isYesterday } from "date-fns";
import { deleteItemLog } from "../../api/ProjectApi";
import GroupedSectionHeader from "../../components/ui/GroupedSectionHeader";
import HoverActionsMenu from "../../components/HoverActionsMenu";
import ActivityAction from "../../actions/ActivityAction";
import { fromJS } from "immutable";
import ConfirmDialog from "../../dialog/ConfirmDialog";
import OctagonAlertIcon from "../../icons/OctagonAlertIcon";
import TriangleAlertIcon from "../../icons/TriangleAlertIcon";
import LayoutAction from "../../actions/LayoutAction";
import { renderItemIcon } from "../component/picker-shared";
import { NotepadText } from "lucide-react";
import ListViewEmptyState from "./ListViewEmptyState";

type NoteData = {
  id: string;
  itemId: string;
  parentItemId: string;
  title: string;
  createdAt?: number;
  author?: string;
  avatarUrl?: string;
  item?: any;
};

const formatWithTime = (date: Date) => {
  if (!isValid(date)) return `-`;
  if (isToday(date)) return `Today`;
  if (isYesterday(date)) return `Yesterday`;
  return format(date, "dd MMM yyyy");
};

const groupByTime = (notes: NoteData[]) => {
  const sorted = notes.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

  const today: NoteData[] = [];
  const thisWeek: NoteData[] = [];
  const months: Record<string, NoteData[]> = {};
  const now = new Date();
  const weekOptions = { weekStartsOn: 1 as const };

  sorted.forEach((t) => {
    const dt = t.createdAt ? new Date(t.createdAt * 1000) : undefined;
    if (!dt) {
      thisWeek.push(t);
      return;
    }
    if (isToday(dt)) {
      today.push(t);
      return;
    }
    if (isSameWeek(dt, now, weekOptions)) {
      thisWeek.push(t);
      return;
    }
    const label = format(dt, "MMMM yyyy");
    (months[label] ||= []).push(t);
  });

  return { today, thisWeek, months };
};

const GroupBlock = ({
  label,
  count,
  isCollapsed,
  onToggle,
  children,
}: {
  label: string;
  count: number;
  isCollapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => {
  return (
    <div className="w-full">
      <GroupedSectionHeader
        label={label}
        count={count}
        isCollapsed={isCollapsed}
        onToggle={onToggle}
      />
      {!isCollapsed && <div className="mt-1">{children}</div>}
    </div>
  );
};

const NoteItem = ({
  note,
  onOpen,
  onDelete,
}: {
  note: NoteData;
  onOpen: (note: NoteData) => void;
  onDelete: (note: NoteData) => void;
}) => {
  const navigate = useNavigate();
  const { projectId }: any = useParams();

  return (
    <div className="relative group flex items-center justify-start gap-2 px-2 py-2 hover:bg-gray-50 transition-colors rounded-md">
      {/* Title */}
      <button
        type="button"
        onClick={() => onOpen(note)}
        className="flex-2 truncate text-left text-sm font-medium text-gray-900 cursor-pointer"
        title={note.title}
      >
        {note.title}
      </button>

      {/* Created by */}
      <div className="flex-1">
        <div className="flex gap-1 items-center">
          <img
            src={note.avatarUrl}
            alt={note.author}
            className="w-5 h-5 rounded-full flex-shrink-0"
          />
          <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{note.author}</p>
        </div>
      </div>

      {/* Item */}
      <div
        className="flex-2 flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer"
        onClick={() => {
          navigate(`/workspaces/${projectId}/items/${note.itemId}?type=${note.item?.type}`);
        }}
      >
        {renderItemIcon({}, note.item?.type)}
        {note.item?.label}
      </div>

      {/* Created at */}
      <div className="flex-1">{formatWithTime(new Date(note.createdAt * 1000))}</div>

      {/* Hover Actions Menu */}
      <HoverActionsMenu onOpen={() => onOpen(note)} onDelete={() => onDelete(note)} />
    </div>
  );
};

const ListViewNote = ({ items, onSearchTasks }: any) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();

  const project = useSelector((state: any) => state.projectReducer.get("project"));

  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const groups = useMemo(() => {
    const notes = items.map((item) => {
      const createdAt = item.getIn(["event", "createdAt"]);
      const summary = item.getIn(["event", "data", "summary"]) || "";
      const members = project.get("members")?.toJS() || [];
      const accountId = item.getIn(["event", "person"]);
      const member = members.find((member) => member.accountId == accountId) || {
        accountId,
        displayName: accountId,
      };
      const itemData = item.getIn(["event", "data", "item"]) || fromJS({});
      return {
        id: item.get("id"),
        itemId: item.get("itemId"),
        parentItemId: itemData.get("value") || itemData.get("id") || item.get("itemId"),
        title: summary,
        author: member.displayName,
        avatarUrl: member.avatarUrl,
        createdAt,
        item: itemData.toJS(),
      };
    });
    return groupByTime(notes);
  }, [items, project]);

  const handleOpen = (note: NoteData) => {
    dispatch(ActivityAction.showActivityDialog(note.itemId, note.id));
  };

  const handleDeleteClick = (note: NoteData) => {
    setShowConfirm({
      show: true,
      title: t("Delete note"),
      description: t("Are you sure you want to remove this note?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "DELETE",
      data: note,
    });
  };

  const handleSubmitConfirm = () => {
    if (showConfirm.type === "DELETE") {
      const { parentItemId, id } = showConfirm.data;
      if (!parentItemId || !id) {
        setShowConfirm({ show: false, title: "", description: "" });
        return;
      }
      deleteItemLog(parentItemId, id)
        .then(() => {
          onSearchTasks({ projectId });
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete note"),
              type: "success",
              message: t("Delete note successfully!"),
            })
          );
        })
        .catch(() => {
          dispatch(
            LayoutAction.setNotification({
              title: t("Delete note"),
              type: "error",
              message: t("Failed to delete note"),
            })
          );
        });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  const handleGroupToggle = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <div className="w-full flex flex-col gap-3">
      {items.size > 0 && (
        <div className="w-full">
          <div className="flex items-center justify-start gap-2 py-2 bg-gray-100">
            <div className="pl-4 flex-2">{t("Note")}</div>
            <div className="flex-1">{t("Created by")}</div>
            <div className="flex-2">{t("Item")}</div>
            <div className="flex-1">{t("Created at")}</div>
          </div>
        </div>
      )}

      {/* Incomplete notes grouped by time */}
      {groups.today.length > 0 && (
        <GroupBlock
          label="Today"
          count={groups.today.length}
          isCollapsed={collapsedGroups.has("Today")}
          onToggle={() => handleGroupToggle("Today")}
        >
          {groups.today.map((note) => (
            <NoteItem key={note.id} note={note} onOpen={handleOpen} onDelete={handleDeleteClick} />
          ))}
        </GroupBlock>
      )}

      {groups.thisWeek.length > 0 && (
        <GroupBlock
          label="This week"
          count={groups.thisWeek.length}
          isCollapsed={collapsedGroups.has("This week")}
          onToggle={() => handleGroupToggle("This week")}
        >
          {groups.thisWeek.map((note) => (
            <NoteItem key={note.id} note={note} onOpen={handleOpen} onDelete={handleDeleteClick} />
          ))}
        </GroupBlock>
      )}

      {Object.entries(groups.months).map(([label, list]) => (
        <GroupBlock
          key={label}
          label={label}
          count={list.length}
          isCollapsed={collapsedGroups.has(label)}
          onToggle={() => handleGroupToggle(label)}
        >
          {list.map((note) => (
            <NoteItem key={note.id} note={note} onOpen={handleOpen} onDelete={handleDeleteClick} />
          ))}
        </GroupBlock>
      ))}

      {items.size === 0 && (
        <ListViewEmptyState
          icon={NotepadText}
          title="Notes"
          description="No notes yet! Create your first note to get started."
          actionLabel="New note"
          onAction={() =>
            dispatch(
              ActivityAction.showActivityDialog(null, null, {
                activityType: "commented",
                summary: "",
              })
            )
          }
        />
      )}

      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmitConfirm}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </div>
  );
};

export default ListViewNote;
